import React, { useEffect, useRef, useState } from 'react';
import { normalizePhoto, PhotoTooLargeError, PHOTO_ACCEPT } from '@/utils/photo-normalize';
import { withPhotoConsent } from '@/components/consent/photo-consent-gate';

/**
 * The multipart body for /api/supabase-upload: the photo plus the parent's consent tick (R3: form field
 * photoConsent=<version>). The route refuses any upload without it (every bucket).
 */
export function supabaseUploadForm(file: File, photoConsent: string | null | undefined): FormData {
  const formData = withPhotoConsent(new FormData(), photoConsent);
  formData.append('file', file);
  return formData;
}

// Shrink big phone photos (and convert HEIC) before upload. Errors use the server's wording so the wizard's
// photo_upload_failed reason codes ('size' / 'type') stay the same.
async function shrinkForUpload(file: File): Promise<File> {
  try {
    return await normalizePhoto(file);
  } catch (e) {
    throw new Error(e instanceof PhotoTooLargeError
      ? 'File too large. Maximum size is 60MB.'
      : 'File type not supported. Please upload a JPEG, PNG, or WebP photo.');
  }
}

/**
 * UploadButton and UploadDropzone components for file uploads
 * These components match the interface expected by the create-book form
 * and work with the existing image server
 */

interface UploadResult {
  url: string;
}

interface UploadProps {
  endpoint?: string;
  onClientUploadComplete?: (results: UploadResult[]) => void;
  onUploadError?: (error: Error) => void;
  onUploadBegin?: () => void;
  className?: string;
  children?: React.ReactNode;
  config?: any;
  appearance?: {
    container?: string;
    uploadIcon?: string;
    label?: string;
    allowedContent?: string;
    button?: string;
  };
  content?: {
    uploadIcon?: () => React.ReactNode;
    label?: string;
    allowedContent?: string;
    button?: (state: { ready: boolean }) => string;
  };
  /** The consent version the parent ticked (sent as photoConsent), or null while unticked. */
  photoConsent?: string | null;
  /**
   * When true, a file picked while unticked is NOT uploaded: it is held, onConsentRequired fires, and the upload
   * continues as soon as photoConsent is set.
   */
  requireConsent?: boolean;
  onConsentRequired?: () => void;
}

/** Holds a file picked before the tick and uploads it once the tick arrives. */
function useConsentHold(props: UploadProps, upload: (file: File) => void) {
  const held = useRef<File | null>(null);
  const uploadRef = useRef(upload);
  uploadRef.current = upload;
  useEffect(() => {
    if (props.photoConsent && held.current) {
      const file = held.current;
      held.current = null;
      uploadRef.current(file);
    }
  }, [props.photoConsent]);
  return (file: File) => {
    if (props.requireConsent && !props.photoConsent) {
      held.current = file;
      props.onConsentRequired?.();
      return;
    }
    held.current = null;
    upload(file);
  };
}

/**
 * UploadButton - A button for uploading files
 */
export const UploadButton: React.FC<UploadProps> = (props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upload = async (picked: File) => {
    if (props.onUploadBegin) {
      props.onUploadBegin();
    }

    try {
      const formData = supabaseUploadForm(await shrinkForUpload(picked), props.photoConsent);

      const endpoint = props.endpoint || 'default';
      
      // Use Supabase upload for consistent storage
      const response = await fetch(`/api/supabase-upload?bucket=customer-photos&folder=${endpoint}`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }
      
      const data = await response.json();
      
      if (props.onClientUploadComplete) {
        props.onClientUploadComplete([{ url: data.url }]);
      }
    } catch (error) {
      if (props.onUploadError) {
        props.onUploadError(error instanceof Error ? error : new Error('Upload failed'));
      }
    }
  };
  const pick = useConsentHold(props, (f) => void upload(f));

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    pick(files[0]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={props.className || ''}>
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        multiple={false}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        {props.children || 'Upload File'}
      </button>
    </div>
  );
};

/**
 * UploadDropzone - A drag-and-drop area for uploading files
 */
export const UploadDropzone: React.FC<UploadProps> = (props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const upload = async (picked: File) => {
    if (props.onUploadBegin) {
      console.log('UploadDropzone: Calling onUploadBegin');
      props.onUploadBegin();
    }
    
    try {
      const formData = supabaseUploadForm(await shrinkForUpload(picked), props.photoConsent);

      const endpoint = props.endpoint || 'default';
      console.log('UploadDropzone: Using endpoint', endpoint);
      
      // Use Supabase upload for consistent storage
      console.log('UploadDropzone: Fetching', `/api/supabase-upload?bucket=customer-photos&folder=${endpoint}`);
      const response = await fetch(`/api/supabase-upload?bucket=customer-photos&folder=${endpoint}`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('UploadDropzone: Upload failed', errorData);
        throw new Error(errorData.error || 'Upload failed');
      }
      
      const data = await response.json();
      console.log('UploadDropzone: Upload successful, response:', data);
      
      if (props.onClientUploadComplete) {
        console.log('UploadDropzone: Calling onClientUploadComplete with', [{ url: data.url }]);
        props.onClientUploadComplete([{ url: data.url }]);
      }
    } catch (error) {
      console.error('UploadDropzone: Error during upload', error);
      if (props.onUploadError) {
        props.onUploadError(error instanceof Error ? error : new Error('Upload failed'));
      }
    }
  };
  // A file picked before the tick is held (not uploaded) and goes up once the box is ticked
  const pick = useConsentHold(props, (f) => void upload(f));

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    console.log('UploadDropzone: File selected', files[0].name);
    pick(files[0]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const event = {
        target: {
          files: files
        }
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(event);
    }
  };
  
  const containerClass = props.appearance?.container || 
    'border-2 border-dashed border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg';
  const buttonClass = props.appearance?.button || 
    'bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors mt-3';
  
  const uploadIcon = props.content?.uploadIcon?.() || null;
  const label = props.content?.label || 'Click to upload a photo';
  const allowedContent = props.content?.allowedContent || 'JPG, PNG, WebP or iPhone HEIC, any size from your phone';
  const buttonText = props.content?.button?.({ ready: true }) || 'Choose Photo';
  
  return (
    <div className={props.className || ''}>
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        multiple={false}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`${containerClass} ${isDragging ? 'border-purple-500 bg-purple-50' : ''} p-8 text-center cursor-pointer`}
      >
        <div className="flex justify-center mb-3">
          {uploadIcon || (
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          )}
        </div>
        
        <p className="text-gray-600 text-sm font-normal mb-1">{label}</p>
        
        <p className="text-xs text-gray-500 mb-4">{allowedContent}</p>
        
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          className={buttonClass}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};
